import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import DriveFileRenameOutlineOutlined from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import BarChartOutlined from '@mui/icons-material/BarChartOutlined'

import { getMissionbyId } from '../../redux/missions/missionActions'
import { getProjetbyMission } from '../../redux/projets/projetActions'
import { getRisquesByMissionId } from '../../redux/risques/risqueActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { Mission } from '../../types/missionTypes'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import { formatTitle } from '../../utils/formatTitle'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatCurrency } from '../../utils/formatNumbers'
import i18n from '../../i18n'

import {
  DetailAccordion,
  DetailButton,
  DetailInfoGrid,
  DetailInfoItem,
  DetailKpiCard,
  DetailKpiGrid,
  DetailPageHead,
} from '../../components/common/detail'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import '../../components/common/ListPageTable.css'
import { ListPageDateCell } from '../../components/common/ListPageCells'

import PipelineListByMission from '../pipeline/PipelineListByMission'
import ProjetListByMission from '../projets/ProjectListByMission'
import ActionListByMission from '../action/ActionListByMission'
import PlanListByMission from '../plan/PlanListByMission'
import IncidentListByMission from '../Incident/incidentByMission'
import AuditListByMission from '../audit/AuditListByMission'
import { writeEntityNav } from '../../utils/entityNav'

type SectionCounts = {
  pipelines: number
  projects: number
  tasks: number
  openTasks: number
  plans: number
  audits: number
  incidents: number
}

type IncidentSummary = {
  count: number
  criticalCount: number
  topLabel?: string
}

const INITIAL_COUNTS: SectionCounts = {
  pipelines: 0,
  projects: 0,
  tasks: 0,
  openTasks: 0,
  plans: 0,
  audits: 0,
  incidents: 0,
}

export default function MissionDetails() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const locale = i18n.language
  const currency = useSelector(selectSpecifications)[0]?.Currency
  const roles = useSelector((state: any) => state.auth.roles)
  const missionFromStore = useSelector(
    (state: RootState) => state.mission.mission
  )
  const projetsResponse = useSelector(
    (state: RootState) => state.projet.ProjetResponse
  )
  const risques = useSelector((state: RootState) => state.risque.risques)

  const [mission, setMission] = useState<Mission>({
    _id: '',
    MissionId: '',
    MissionName: '',
    Description: '',
    Budget: 0,
    CreatedBy: '',
  })
  const [open, setOpen] = useState<Record<string, boolean>>({
    info: true,
    pipelines: true,
    projects: false,
    tasks: false,
    plans: false,
    audits: false,
    incidents: false,
  })
  const [counts, setCounts] = useState<SectionCounts>(INITIAL_COUNTS)
  const [incidentSummary, setIncidentSummary] = useState<IncidentSummary>({
    count: 0,
    criticalCount: 0,
  })

  useEffect(() => {
    if (id) {
      dispatch(getMissionbyId(id))
      dispatch(
        getProjetbyMission({ MissionId: id, page: 1, pageSize: 100 })
      )
      dispatch(getRisquesByMissionId(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (missionFromStore) {
      setMission({
        _id: missionFromStore._id,
        MissionId: missionFromStore.MissionId,
        MissionName: missionFromStore.MissionName,
        Description: missionFromStore.Description,
        Budget: missionFromStore.Budget,
        CreatedBy: missionFromStore.CreatedBy,
        CreationDate: missionFromStore.CreationDate,
      })
      if (missionFromStore._id) {
        writeEntityNav({
          missionId: missionFromStore._id,
          missionLabel: formatTitle(missionFromStore.MissionName || ''),
        })
      }
    }
  }, [missionFromStore])

  const setCount = useCallback(
    (key: keyof SectionCounts) => (value: number) => {
      setCounts((prev) =>
        prev[key] === value ? prev : { ...prev, [key]: value }
      )
    },
    []
  )

  const toggle = (key: string) => () => {
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const allocatedBudget = useMemo(() => {
    const projets = projetsResponse?.projets ?? []
    return projets.reduce((sum, p) => sum + (Number(p.Budget) || 0), 0)
  }, [projetsResponse?.projets])

  const riskRows = useMemo(() => {
    if (!Array.isArray(risques)) return []
    return risques.filter((r: any) => r && r._id)
  }, [risques])

  const riskCount = riskRows.length
  const firstPlanName =
    riskRows.find((r: any) => r?.PlanName)?.PlanName || undefined

  const canUpdate =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)

  const canAnalyze =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.DSSI) ||
    hasRole(roles, UserRole.ADMIN)

  const title = formatTitle(mission.MissionName) || t('Mission')

  const budgetDesc =
    allocatedBudget > 0
      ? t('missionDetailsPage.kpi.budgetAllocated', {
          amount: formatCurrency(allocatedBudget, currency, {
            locale,
            useIntl: true,
          }),
          defaultValue: `{{amount}} allocated to projects`,
        })
      : t('missionDetailsPage.kpi.budgetNone', {
          defaultValue: 'Nothing allocated to a project yet',
        })

  const openTasksDesc =
    counts.openTasks === 0
      ? t('missionDetailsPage.kpi.tasksNone', {
          defaultValue: 'No open tasks right now',
        })
      : t('missionDetailsPage.kpi.tasksOpen', {
          count: counts.openTasks,
          defaultValue: '{{count}} open in this mission',
        })

  const risksDesc =
    riskCount === 0
      ? t('missionDetailsPage.kpi.risksNone', {
          defaultValue: 'No risks recorded for this mission',
        })
      : firstPlanName
        ? t('missionDetailsPage.kpi.risksTreated', {
            plan: firstPlanName,
            defaultValue: 'Treated by {{plan}}',
          })
        : t('missionDetailsPage.kpi.risksCount', {
            count: riskCount,
            defaultValue: '{{count}} risks in scope',
          })

  const incidentsDesc =
    incidentSummary.count === 0
      ? t('missionDetailsPage.kpi.incidentsNone', {
          defaultValue: 'No incidents recorded',
        })
      : incidentSummary.topLabel ||
        t('missionDetailsPage.kpi.incidentsCount', {
          count: incidentSummary.count,
          defaultValue: '{{count}} incident(s) recorded',
        })

  return (
    <div className="detail-page">
      <DetailPageHead
        eyebrow={t('missionDetailsPage.eyebrow', {
          defaultValue: 'Management · Missions',
        })}
        title={title}
        subtitle={t('missionDetailsPage.subtitle', {
          defaultValue:
            'Everything attached to this mission, in one place. Open a section to work inside it.',
        })}
        actions={
          <>
            {canAnalyze ? (
              <DetailButton
                variant="ghost"
                onClick={() => navigate(`/mission/view-analysis/${id}`)}
              >
                <BarChartOutlined sx={{ fontSize: 16 }} />
                {t('buttons.viewAnalysis', { defaultValue: 'View Analysis' })}
              </DetailButton>
            ) : null}
            {canUpdate ? (
              <DetailButton onClick={() => navigate(`/mission/${mission._id}`)}>
                <DriveFileRenameOutlineOutlined sx={{ fontSize: 16 }} />
                {t('missionDetailsPage.btn.update')}
              </DetailButton>
            ) : null}
          </>
        }
      />

      <DetailKpiGrid>
        <DetailKpiCard
          label={t('missionDetailsPage.kpi.budget', { defaultValue: 'Budget' })}
          value={formatCurrency(mission.Budget ?? 0, currency, {
            locale,
            useIntl: true,
          })}
          valueSize="sm"
          description={budgetDesc}
        />
        <DetailKpiCard
          label={t('missionDetailsPage.kpi.openTasks', {
            defaultValue: 'Open tasks',
          })}
          value={counts.openTasks}
          description={openTasksDesc}
        />
        <DetailKpiCard
          label={t('missionDetailsPage.kpi.risks', { defaultValue: 'Risks' })}
          value={riskCount}
          description={risksDesc}
        />
        <DetailKpiCard
          label={t('missionDetailsPage.kpi.incidents', {
            defaultValue: 'Incidents',
          })}
          value={incidentSummary.count}
          valueTone={
            incidentSummary.criticalCount > 0 ? 'coral' : 'default'
          }
          description={incidentsDesc}
        />
      </DetailKpiGrid>

      <DetailAccordion
        title={t('missionDetailsPage.missionPanel.title')}
        open={open.info}
        onToggle={toggle('info')}
      >
        <DetailInfoGrid>
          <DetailInfoItem
            label={t('missionDetailsPage.missionPanel.name')}
            value={formatTitle(mission.MissionName)}
          />
          <DetailInfoItem
            label={t('missionDetailsPage.missionPanel.budget')}
            mono
            value={formatCurrency(mission.Budget ?? 0, currency, {
              locale,
              useIntl: true,
            })}
          />
          <DetailInfoItem
            label={t('missionDetailsPage.missionPanel.description')}
            value={<ListPageDescription value={mission.Description} />}
          />
          <DetailInfoItem
            label={t('missionDetailsPage.missionPanel.creator')}
            value={mission.CreatedBy || '—'}
          />
          <DetailInfoItem
            label={t('missionDetailsPage.missionPanel.created', {
              defaultValue: 'Created',
            })}
            mono
            value={
              <ListPageDateCell
                value={mission.CreationDate}
                locale={locale}
              />
            }
          />
        </DetailInfoGrid>
      </DetailAccordion>

      <DetailAccordion
        title={t('missionDetailsPage.accordion.pipelines')}
        count={counts.pipelines}
        open={open.pipelines}
        onToggle={toggle('pipelines')}
      >
        <PipelineListByMission
          IdMission={id || ''}
          missionName={mission.MissionName}
          onCountChange={setCount('pipelines')}
        />
      </DetailAccordion>

      <DetailAccordion
        title={t('missionDetailsPage.accordion.projects')}
        count={counts.projects}
        open={open.projects}
        onToggle={toggle('projects')}
      >
        <ProjetListByMission
          IdMission={id || ''}
          onCountChange={setCount('projects')}
        />
      </DetailAccordion>

      <DetailAccordion
        title={t('missionDetailsPage.accordion.tasks')}
        count={counts.tasks}
        open={open.tasks}
        onToggle={toggle('tasks')}
      >
        <ActionListByMission
          IdMission={id || ''}
          onCountChange={setCount('tasks')}
          onOpenCountChange={setCount('openTasks')}
        />
      </DetailAccordion>

      <DetailAccordion
        title={t('missionDetailsPage.accordion.riskTreatmentPlans')}
        count={counts.plans}
        open={open.plans}
        onToggle={toggle('plans')}
      >
        <PlanListByMission
          IdMission={id || ''}
          onCountChange={setCount('plans')}
        />
      </DetailAccordion>

      <DetailAccordion
        title={t('missionDetailsPage.accordion.audits')}
        count={counts.audits}
        open={open.audits}
        onToggle={toggle('audits')}
      >
        <AuditListByMission
          MissionId={id || ''}
          onCountChange={setCount('audits')}
        />
      </DetailAccordion>

      <DetailAccordion
        title={t('missionDetailsPage.accordion.incidents')}
        count={counts.incidents}
        open={open.incidents}
        onToggle={toggle('incidents')}
      >
        <IncidentListByMission
          MissionId={id || ''}
          onCountChange={setCount('incidents')}
          onSummaryChange={setIncidentSummary}
        />
      </DetailAccordion>
    </div>
  )
}
